import React, { useEffect, useRef, useState } from "react";
import { getDevice } from "../../../../../../utils/device";
import CustomPill from "../../../../../Shared/CustomPill/CustomPill";
import { WebViewPopover } from "../../../../Diary/SubHeader/WebViewPopover";
import { WebViewTooltip } from "../../../../Diary/SubHeader/WebViewTooltip";

interface PillData {
  key: string;
  label: string;
  backgroundColour?: string;
  labelColour?: string;
  softenColour?: boolean;
  className?: string;
  style?: React.CSSProperties;
}

interface PillsWithOverflowProps {
  pills: PillData[];
  maxHeight?: number;
  maxPills?: number;
}

export const PillsWithOverflow: React.FC<PillsWithOverflowProps> = ({
  pills,
  maxHeight = 120,
  maxPills
}) => {
  const [visiblePills, setVisiblePills] = useState<PillData[]>(pills);
  const [hiddenPills, setHiddenPills] = useState<PillData[]>([]);
  const [showEllipsis, setShowEllipsis] = useState(false);
  const [showPopover, setShowPopover] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const isDesktop = getDevice() === "desktop";

  useEffect(() => {
    if (!containerRef.current || pills.length === 0) {
      setVisiblePills(pills);
      setHiddenPills([]);
      setShowEllipsis(false);
      return;
    }

    const measurePills = () => {
      const container = containerRef.current;
      if (!container) return;

      if (maxPills && pills.length > maxPills) {
        setVisiblePills(pills.slice(0, maxPills));
        setHiddenPills(pills.slice(maxPills));
        setShowEllipsis(true);
        return;
      }

      const tempContainer = document.createElement("div");
      tempContainer.style.position = "absolute";
      tempContainer.style.visibility = "hidden";
      tempContainer.style.width = `${container.offsetWidth}px`;
      tempContainer.style.display = "flex";
      tempContainer.style.flexWrap = "wrap";
      tempContainer.style.gap = "4px";

      try {
        document.body.appendChild(tempContainer);

        let visibleCount = 0;

        for (let i = 0; i < pills.length; i++) {
          const pillElement = document.createElement("div");
          pillElement.className = "CustomPill";
          pillElement.innerHTML = `
            <span class="Label">${pills[i].label}</span>
            <div class="Background${pills[i].softenColour ? " SoftenColour" : ""}" style="background-color: ${pills[i].backgroundColour || "#e5e7eb"}"></div>
          `;
          tempContainer.appendChild(pillElement);

          const newHeight = tempContainer.offsetHeight;
          if (newHeight > maxHeight) {
            break;
          }

          visibleCount = i + 1;
        }

        if (visibleCount < pills.length) {
          setVisiblePills(pills.slice(0, visibleCount - 1));
          setHiddenPills(pills.slice(visibleCount - 1));
          setShowEllipsis(true);
        } else {
          setVisiblePills(pills);
          setHiddenPills([]);
          setShowEllipsis(false);
        }
      } catch (error) {
        console.warn("Error measuring pills:", error);
        // Fallback to showing all pills
        setVisiblePills(pills);
        setHiddenPills([]);
        setShowEllipsis(false);
      } finally {
        // Always clean up the temporary container
        if (tempContainer?.parentNode) {
          try {
            document.body.removeChild(tempContainer);
          } catch (cleanupError) {
            console.warn("Error cleaning up temp container:", cleanupError);
          }
        }
      }
    };

    const timeoutId = setTimeout(measurePills, 0);
    return () => clearTimeout(timeoutId);
  }, [pills, maxHeight, maxPills]);

  if (pills.length === 0) {
    return null;
  }

  return (
    <div ref={containerRef} className="pills-container">
      {visiblePills.map((pill, index) => (
        <WebViewTooltip
          key={`${pill.key}-${index}`}
          content={pill.label}
          side="bottom"
          showOnlyWhenTruncated={true}
        >
          <CustomPill
            label={pill.label}
            backgroundColour={pill.backgroundColour}
            labelColour={pill.labelColour}
            softenColour={pill.softenColour}
            className={pill.className}
            style={pill.style}
          />
        </WebViewTooltip>
      ))}
      {showEllipsis &&
        hiddenPills.length > 0 &&
        (isDesktop ? (
          <WebViewTooltip
            content={
              <div className="pills-popover">
                {hiddenPills.map((pill, index) => (
                  <CustomPill
                    key={`${pill.key}-${index}`}
                    label={pill.label}
                    backgroundColour={pill.backgroundColour}
                    labelColour={pill.labelColour}
                    softenColour={pill.softenColour}
                  />
                ))}
              </div>
            }
            side="bottom"
            sideOffset={8}
          >
            <div className="pills-ellipsis">+{hiddenPills.length}</div>
          </WebViewTooltip>
        ) : (
          <WebViewPopover
            open={showPopover}
            onOpenChange={setShowPopover}
            side="bottom"
            sideOffset={8}
            content={
              <div className="pills-popover">
                {hiddenPills.map((pill, index) => (
                  <CustomPill
                    key={`${pill.key}-${index}`}
                    label={pill.label}
                    backgroundColour={pill.backgroundColour}
                    labelColour={pill.labelColour}
                    softenColour={pill.softenColour}
                  />
                ))}
              </div>
            }
          >
            <button
              type="button"
              className="pills-ellipsis"
              onClick={() => setShowPopover(!showPopover)}
            >
              +{hiddenPills.length}
            </button>
          </WebViewPopover>
        ))}
    </div>
  );
};
